<script setup>
/**
 * Where the payer lands after the payment page (`?order=<id>`; the payments service also
 * appends its own `session_id`, `payment_id` and `result`, which this page ignores).
 *
 * It asks for the order, and the sidecar asks the payments service while the order is
 * open, so this page usually shows the outcome on its first read. While the bank has not
 * confirmed it polls on the section's job ladder; the credits arrive on their own either
 * way (the sidecar also hears the push and reconciles every five minutes).
 */
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import WasloPageLayout from '../components/WasloPageLayout.vue';
import { useWasloI18n } from '../i18n.js';
import { useWasloAccountId } from '../composables/useWasloAccountId.js';
import { useJobPoller } from '../composables/useJobPoller.js';
import { wasloApi, WASLO_ERROR_CODES } from '../api/client.js';

const { t } = useWasloI18n();
const route = useRoute();
const router = useRouter();
const accountId = useWasloAccountId();

const ORDER_ID = /^[1-9][0-9]{0,9}$/;
const WAITING = new Set(['creating', 'pending']);
const NOT_PAID = new Set(['failed', 'cancelled', 'expired', 'start_failed']);

const orderId = computed(() => {
  const raw = route?.query?.order;
  return typeof raw === 'string' && ORDER_ID.test(raw) ? raw : '';
});
const order = ref(null);
const loading = ref(true);
const missing = ref(false);
const failure = ref('');

const fetchOrder = async () => {
  if (accountId.value === null || orderId.value === '') {
    missing.value = true;
    return;
  }
  try {
    order.value = await wasloApi.getCreditOrder(accountId.value, orderId.value);
    failure.value = '';
  } catch (error) {
    if (error?.code === WASLO_ERROR_CODES.NOT_FOUND) missing.value = true;
    else failure.value = t('WASLO.BUY_CREDITS.RETURN.LOAD_FAILED');
  }
};

const isWaiting = () =>
  !missing.value && (order.value === null || WAITING.has(order.value.status));

const poller = useJobPoller(async () => {
  await fetchOrder();
  if (!isWaiting()) poller.stop();
});

onMounted(async () => {
  await fetchOrder();
  loading.value = false;
  if (isWaiting()) poller.start();
});

const outcome = computed(() => {
  if (missing.value) return 'missing';
  const status = order.value?.status;
  if (status === 'paid') return 'paid';
  if (status === 'mismatch') return 'check';
  if (NOT_PAID.has(status)) return 'not_paid';
  return 'waiting';
});

const paidLine = computed(() =>
  order.value
    ? t('WASLO.BUY_CREDITS.RETURN.PAID', {
        credits: t('WASLO.AI_CONFIRM.CREDITS', { count: order.value.credits }),
      })
    : ''
);

const toOverview = () =>
  router.push({
    name: 'waslo_overview',
    params: { accountId: accountId.value },
  });
const toBuy = () =>
  router.push({
    name: 'waslo_credits_buy',
    params: { accountId: accountId.value },
  });
</script>

<template>
  <WasloPageLayout
    :header-title="t('WASLO.BUY_CREDITS.RETURN.TITLE')"
    :is-fetching="loading"
  >
    <template #body>
      <div
        class="flex flex-col gap-4 pb-10 max-w-[62ch]"
        data-waslo-credits-return
      >
        <template v-if="outcome === 'paid'">
          <Banner color="teal" data-waslo-credits-paid>{{ paidLine }}</Banner>
          <div>
            <Button
              size="sm"
              :label="t('WASLO.BUY_CREDITS.RETURN.BACK')"
              data-waslo-credits-back
              @click="toOverview"
            />
          </div>
        </template>
        <template v-else-if="outcome === 'not_paid'">
          <p class="mb-0 text-sm text-n-slate-12" data-waslo-credits-not-paid>
            {{ t('WASLO.BUY_CREDITS.RETURN.NOT_PAID') }}
          </p>
          <div>
            <Button
              size="sm"
              :label="t('WASLO.BUY_CREDITS.RETURN.TRY_AGAIN')"
              data-waslo-credits-try-again
              @click="toBuy"
            />
          </div>
        </template>
        <Banner
          v-else-if="outcome === 'check'"
          color="amber"
          data-waslo-credits-check
        >
          {{ t('WASLO.BUY_CREDITS.RETURN.CHECK') }}
        </Banner>
        <template v-else-if="outcome === 'missing'">
          <p class="mb-0 text-sm text-n-slate-12" data-waslo-credits-missing>
            {{ t('WASLO.BUY_CREDITS.RETURN.MISSING') }}
          </p>
          <div>
            <Button
              size="sm"
              variant="faded"
              color="slate"
              :label="t('WASLO.BUY_CREDITS.RETURN.BACK')"
              @click="toOverview"
            />
          </div>
        </template>
        <template v-else>
          <p class="mb-0 text-sm text-n-slate-12" data-waslo-credits-waiting>
            {{ t('WASLO.BUY_CREDITS.RETURN.WAITING') }}
          </p>
          <template v-if="poller.isStalled.value">
            <p class="mb-0 text-sm text-n-slate-11" data-waslo-credits-stalled>
              {{ t('WASLO.BUY_CREDITS.RETURN.STALLED') }}
            </p>
            <div>
              <Button
                size="sm"
                variant="faded"
                color="slate"
                :label="t('WASLO.BUY_CREDITS.RETURN.CHECK_AGAIN')"
                @click="poller.resume()"
              />
            </div>
          </template>
        </template>
        <p
          v-if="failure"
          class="mb-0 text-sm text-n-ruby-11"
          data-waslo-credits-failure
        >
          {{ failure }}
        </p>
      </div>
    </template>
  </WasloPageLayout>
</template>
